import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import styles from './styles/Banners.module.scss';

import { OmaLummeApp } from '../../../omaLummeContext';

import Button from '../../Common/Button';
import Dialog from '../../Common/Dialog';
import Loading from '../../Common/Loading';

// Actions.
import { fetchBasicFeeAndMarginToday, postProductChange } from '../../../actions';

// Types.
import { BasicFeeAndMarginToday, OmaLummeCampaign } from '../../../types/contract';

// Utils.
import { fetchCampaignOmaLummePrices } from '../../../utils/contractManagement';
import { isActiveUserB2B } from '../../../utils/customer';
import { PriceFixCampaignContractTemplateNo, PriceFixTariffNo } from '../../../utils/contracts';
import { roundNumber, setPriceFormat } from '../../../utils/billing';
import { generateAddress } from '../../../utils/address';

const PriceFixingBanner: React.FunctionComponent = () => {
  const { t } = useTranslation();
  const { config, users, selectedMeteringPoint, selectedMeteringPointContract } = OmaLummeApp.useState();
  const { activeUser } = users;

  const [campaigns, setCampaigns] = React.useState<OmaLummeCampaign[] | undefined>();
  const [basicFeeAndMargin, setBasicFeeAndMargin] = React.useState<BasicFeeAndMarginToday[] | undefined>();

  const [priceFixBannerVisible, setPriceFixBannerVisible] = React.useState<boolean>(true);
  const [priceFixDialogVisible, setPriceFixDialogVisible] = React.useState<boolean>(false);
  const [priceFixDialogTerms, setPriceFixDialogTerms] = React.useState<boolean>(false);
  const [changingProductToPriceFixing, setChangingProductToPriceFixing] = React.useState<boolean>(false);
  const [productChangedToPriceFixing, setProductChangedToPriceFixing] = React.useState<boolean>(false);

  React.useEffect(() => {
    async function fetchBasicFeeAndMargin(): Promise<void> {
      if (selectedMeteringPointContract) {
        const fetchedFeeAndMargin = await fetchBasicFeeAndMarginToday();

        setBasicFeeAndMargin(fetchedFeeAndMargin);
      } else {
        setBasicFeeAndMargin(undefined);
      }
    }
    async function fetchCampaigns(): Promise<void> {
      const campaign = import.meta.env.VITE_MANAGE_CONTRACTS_NEW_CONTRACT_LUMME_CAMPAIGN_ID;
      const campaignPromise = fetchCampaignOmaLummePrices(campaign);
      campaignPromise.then((campaigns) => {
        setCampaigns(campaigns);
      });
    }
    fetchBasicFeeAndMargin();
    fetchCampaigns();
  }, [activeUser, selectedMeteringPointContract]);

  const isB2B = isActiveUserB2B(users);

  const priceFixFeeAddOnElement =
    basicFeeAndMargin && basicFeeAndMargin.length > 0 ? basicFeeAndMargin?.find((f) => f.priceText === 'BasicFeeAddon') : undefined;
  const priceFixFeeAddOn = priceFixFeeAddOnElement ? priceFixFeeAddOnElement[isB2B ? 'price' : 'priceInclVat'] : undefined;

  const regularSpotContractTariffNo = 7;
  const priceFixCampaignContractTemplateNo = PriceFixCampaignContractTemplateNo;
  const priceFixCampaignTariffNo = PriceFixTariffNo;

  const priceFixOfferCampaign =
    campaigns && campaigns.length > 0 ? campaigns.find((c) => c.contractTemplateNo === priceFixCampaignContractTemplateNo) : undefined;

  const priceFixTariffElement =
    priceFixOfferCampaign &&
    priceFixOfferCampaign.tariffs.find((t) => t.tariffNo === priceFixCampaignTariffNo)?.tariffElements.find((e) => e.elementNo === 28);

  const productChangeToPriceFixing = async (): Promise<void> => {
    if (selectedMeteringPointContract) {
      setChangingProductToPriceFixing(true);
      const productChangeRequest = await postProductChange(
        'priceFixing',
        selectedMeteringPointContract.customerId,
        users.activeUser.userId,
        selectedMeteringPointContract.contractNo,
      );
      if (productChangeRequest.status === 200) {
        setChangingProductToPriceFixing(false);
        setProductChangedToPriceFixing(true);
      }
    }
  };

  const priceFixButtonText = (
    <>
      {priceFixTariffElement && priceFixTariffElement.discountInfo
        ? `${t(`landingPage:priceFixBanner.campaignOfferCta`)} +${setPriceFormat(
            roundNumber(priceFixTariffElement[isB2B ? 'price' : 'priceInclVat']),
          )} ${t(`landingPage:priceFixBanner.priceUnit`)} (${priceFixTariffElement.discountInfo.duration} ${t(
            `landingPage:priceFixBanner.durationUnit`,
          )})`
        : `${t(`landingPage:priceFixBanner.cta`)} +${setPriceFormat(roundNumber(priceFixFeeAddOn ?? 0))} ${t(
            `landingPage:priceFixBanner.priceUnit`,
          )}`}
    </>
  );

  return (
    <>
      {config?.priceFixing === true &&
        config?.priceFixingPromotion === true &&
        selectedMeteringPoint &&
        selectedMeteringPointContract &&
        selectedMeteringPointContract.activeDeliveries?.find((d) => d.tariffNo === regularSpotContractTariffNo) &&
        priceFixFeeAddOn !== undefined &&
        priceFixBannerVisible && (
          <>
            <div className={styles.greenBanner}>
              <div>
                <button className={styles.dismiss} onClick={() => setPriceFixBannerVisible(false)}>
                  <span>{t(`common:general.close`)}</span>
                </button>
                <h2 className="h1">{t(`landingPage:priceFixBanner.title`)}</h2>

                <p>{t(`landingPage:priceFixBanner.description`)}</p>

                {priceFixTariffElement && priceFixTariffElement.discountInfo && (
                  <p>
                    <strong>
                      <Trans
                        i18nKey="landingPage:priceFixBanner.campaignOfferTitle"
                        values={{
                          campaignPrice: setPriceFormat(roundNumber(priceFixTariffElement[isB2B ? 'price' : 'priceInclVat'])),
                          duration: setPriceFormat(roundNumber(priceFixTariffElement.discountInfo.duration)),
                        }}
                      />
                    </strong>
                    <br />
                    <Trans
                      i18nKey="landingPage:priceFixBanner.campaignOfferDescription"
                      values={{
                        duration: setPriceFormat(roundNumber(priceFixTariffElement.discountInfo.duration)),
                        normalPrice: setPriceFormat(roundNumber(priceFixFeeAddOn)),
                      }}
                    />
                  </p>
                )}

                <div className={styles.bannerButtons}>
                  <Button className="buttonTertiary" link="https://www.lumme-energia.fi/sahkosopimus/porssisahko/hintaturva" newTab>
                    {t(`common:general.readMore`)}
                  </Button>
                  <Button className="buttonTertiaryLime" onClick={() => setPriceFixDialogVisible(true)}>
                    {priceFixButtonText}
                  </Button>
                </div>
              </div>
            </div>
            <Dialog rounded openModal={priceFixDialogVisible} closeModal={() => setPriceFixDialogVisible(false)}>
              <div className={styles.priceFixDialog}>
                {!productChangedToPriceFixing && (
                  <>
                    <button className={styles.dismiss} onClick={() => setPriceFixDialogVisible(false)}>
                      <span>{t(`common:general.close`)}</span>
                    </button>
                    <h2>{t(`landingPage:priceFixDialog.title`)}</h2>
                    <p>{t(`landingPage:priceFixDialog.description`)}</p>
                    <p>{t(`landingPage:priceFixDialog.activateForThisMeteringPoint`)}</p>
                    <div className={styles.priceFixDialogAddress}>{generateAddress(selectedMeteringPointContract.meteringPoint, false)}</div>
                    <div className={`form-item ${styles.priceFixTerms}`}>
                      <input
                        type="checkbox"
                        id="acceptTerms"
                        onChange={(): void => setPriceFixDialogTerms(!priceFixDialogTerms)}
                        checked={priceFixDialogTerms}
                      />
                      <div className="form-item-input"></div>
                      <label htmlFor="acceptTerms">
                        <Trans i18nKey="landingPage:priceFixDialog.acceptTerms" values={{ terms: t(`landingPage:priceFixDialog.terms`) }}>
                          .
                          <a
                            href="https://www.lumme-energia.fi/sahkosopimus/porssisahko-hintaturva/sopimusehdot"
                            target="_blank"
                            rel="noopener noreferrer"
                          ></a>
                        </Trans>
                      </label>
                    </div>
                    <Button
                      disabled={!priceFixDialogTerms || changingProductToPriceFixing}
                      onClick={(e) => {
                        e.preventDefault();
                        // @ts-ignore
                        if (e.target.ariaDisabled === 'false') {
                          productChangeToPriceFixing();
                        }
                      }}
                    >
                      {priceFixButtonText}
                    </Button>
                    {priceFixTariffElement && priceFixTariffElement.discountInfo && (
                      <p className={styles.priceFixDisclaimer}>
                        <Trans
                          i18nKey="landingPage:priceFixDialog.campaignOfferDisclaimer"
                          values={{
                            duration: setPriceFormat(roundNumber(priceFixTariffElement.discountInfo.duration)),
                            normalPrice: setPriceFormat(roundNumber(priceFixFeeAddOn)),
                          }}
                        />
                      </p>
                    )}
                    {changingProductToPriceFixing && <Loading size="small" />}
                  </>
                )}
                {productChangedToPriceFixing && (
                  <>
                    <button
                      className={styles.dismiss}
                      onClick={() => {
                        setPriceFixDialogVisible(false);
                        setPriceFixBannerVisible(false);
                      }}
                    >
                      <span>{t(`common:general.close`)}</span>
                    </button>
                    <h2 className={styles.priceFixSuccess}>{t(`landingPage:priceFixDialog.confirmationTitle`)}</h2>
                    <p>{t(`landingPage:priceFixDialog.confirmationDescription`)}</p>
                    <Button
                      onClick={() => {
                        setPriceFixDialogVisible(false);
                        setPriceFixBannerVisible(false);
                      }}
                    >
                      {t(`landingPage:priceFixDialog.confirmationCta`)}
                    </Button>
                  </>
                )}
              </div>
            </Dialog>
          </>
        )}
    </>
  );
};

export default PriceFixingBanner;
